import { FlexRow } from '@fox/layout';
import { Placement } from '@popperjs/core';
import React from 'react';
import styled from 'styled-components';
import { Button } from '../button';
import { PromiseButton } from '../promise-button';
import { Typography } from '../typography';
import { IPopoverProps, Popover } from './index';

const PLACEMENT_MAP: Record<string, Placement> = {
	topRight: 'top-start',
	bottomRight: 'bottom-start',
	bottomLeft: 'bottom-end',
	rightTop: 'top-start',
	top: 'top',
	bottom: 'bottom',
	left: 'left',
	right: 'right'
};

export type ConfirmPopoverProps = Omit<IPopoverProps, 'overlay' | 'placement'> & {
	message: React.ReactNode;
	messageIsComponent?: boolean;
	disabled?: boolean;
	confirm: (() => Promise<void>) | VoidFunction;
	cancel?: () => void;
	confirmButtonText?: string;
	deleteButtonText?: string;
	cancelButtonText?: string;
	maxWidth?: string | number;
	padding?: string;
	paddingButtonGroup?: string;
	placement?: string;
	bypassConfirmation?: boolean;
	banner?: React.ReactNode;
};

export const DeletePopover = (props: ConfirmPopoverProps) => {
	const { children, ...rest } = props;
	const [visible, setVisible] = React.useState(false);
	const placement = PLACEMENT_MAP[props?.placement ?? ''] ?? 'bottom-start';

	return (
		<Popover
			onVisibleChange={v => props.disabled !== true && setVisible(v)}
			visible={visible}
			{...rest}
			placement={placement}
			overlay={<DeleteOverlay setVisible={setVisible} {...props} />}
		>
			<div>{children}</div>
		</Popover>
	);
};

export const DeleteOverlay = (props: ConfirmPopoverProps & { setVisible: (f: boolean) => void }) => {
	return (
		<ConfirmPopoverOverlay>
			<Typography style={{ paddingBottom: '16px ' }}>{props.message}</Typography>

			<div
				style={{
					display: 'flex',
					marginLeft: 'auto',
					gap: '1rem'
				}}
			>
				<Button
					//outlined
					onClick={e => {
						e.stopPropagation();
						if (props.cancel) props.cancel();
						else props.setVisible(false);
					}}
				>
					{props.cancelButtonText ?? 'Cancel'}
				</Button>

				<PromiseButton
					color="error"
					variant="primary"
					onClick={async e => {
						e.stopPropagation();

						await Promise.resolve(props.confirm());

						props.setVisible(false);
					}}
				>
					{props.deleteButtonText ?? 'Delete'}
				</PromiseButton>
			</div>
		</ConfirmPopoverOverlay>
	);
};

const CONFIRM_POPOVER_MAXWIDTH = '300px';

export const ConfirmPopoverOverlay = styled.div<{ $maxWidth?: string | number; $padding?: string }>`
	max-width: ${props => props.$maxWidth ?? CONFIRM_POPOVER_MAXWIDTH};
	padding: ${props => props.$padding ?? '1rem'};
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export const ConfirmPopoverBanner = styled.div<{ $maxWidth?: string | number }>`
	max-width: ${props => props.$maxWidth ?? CONFIRM_POPOVER_MAXWIDTH};
`;

export const ConfirmPopover = (props: ConfirmPopoverProps) => {
	const [visible, setVisible] = React.useState(false);

	const placement = PLACEMENT_MAP[props.placement ?? ''] ?? 'bottom-start';

	if (props.bypassConfirmation) {
		return (
			<div
				onClick={async () => {
					await Promise.resolve(props.confirm());
				}}
			>
				{props.children}
			</div>
		);
	}

	return (
		<Popover
			onVisibleChange={v => props.disabled !== true && setVisible(v)}
			visible={visible}
			{...props}
			placement={placement}
			overlay={
				<>
					{props.banner ? <ConfirmPopoverBanner $maxWidth={props.maxWidth}>{props.banner}</ConfirmPopoverBanner> : <></>}
					<ConfirmPopoverOverlay $maxWidth={props.maxWidth} $padding={props.padding}>
						{props?.messageIsComponent ? props.message : <Typography>{props.message}</Typography>}
						<FlexRow
							style={{
								alignItems: 'center',
								justifyContent: 'flex-end',
								gap: '1rem',
								padding: props?.paddingButtonGroup ?? ''
							}}
						>
							<Button
								//outlined
								onClick={e => {
									e.stopPropagation();
									if (props.cancel) props.cancel();
									else setVisible(false);
								}}
							>
								Cancel
							</Button>

							<Button
								variant="primary"
								color="primary"
								onClick={async e => {
									e.stopPropagation();
									await Promise.resolve(props.confirm());
									setVisible(false);
								}}
							>
								{props?.confirmButtonText ?? 'Confirm'}
							</Button>
						</FlexRow>
					</ConfirmPopoverOverlay>
				</>
			}
		>
			<div>{props.children}</div>
		</Popover>
	);
};
